import { z } from 'zod';
import { mentionCandidateSchema } from './polls';

export * from './polls';

/**
 * Contracten van de module interaction: liken en reageren op een bericht.
 *
 * Dit bestand dekt de eerste slice (Stap 12b): een like is een teller zonder gewicht, een reactie
 * is platte tekst zonder threading. Wat Stap 26 erbij bracht — peilingen, @-vermeldingen,
 * moderatie — staat in `polls.ts` en wordt hier doorgeéxporteerd; alleen de velden die een
 * bestaande vorm raken (`canDelete`, `mentions`, `reportedByMe`) staan hieronder.
 *
 * Namen bij een like zijn hier bewust niet verborgen: een like draagt geen inhoud en is geen
 * monitoring van of iemand een instructie las (dat is `reach`, en dat blijft wél achter
 * ADR-0014). Wie liket, doet dat zichtbaar — zoals in elk vergelijkbaar product.
 */

export const likeSummarySchema = z.object({
  postId: z.guid(),
  count: z.number().int(),
  likedByMe: z.boolean(),
});
export type LikeSummary = z.infer<typeof likeSummarySchema>;

export const likeSummaryListSchema = z.object({ items: z.array(likeSummarySchema) });
export type LikeSummaryList = z.infer<typeof likeSummaryListSchema>;

export const commentSchema = z.object({
  id: z.guid(),
  postId: z.guid(),
  /**
   * De reactie waarop dit een antwoord is, of `null` bij een gewone reactie (SCH-1).
   *
   * **Eén niveau diep.** Een antwoord op een antwoord hangt aan dezelfde ouder; dat normaliseert de
   * database met een trigger, niet de hoop. Dieper wordt op een telefoon van 390 px onleesbaar en
   * vraagt inklappen, sorteren en een "toon meer" — drie dingen die niemand gevraagd heeft.
   */
  parentId: z.guid().nullable(),
  authorId: z.guid(),
  authorName: z.string(),
  body: z.string(),
  createdAt: z.string().datetime(),
  /** Van de aanvrager zelf: bepaalt of de verwijderknop verschijnt. */
  mine: z.boolean(),
  /**
   * Of de aanvrager deze reactie mag weghalen (Stap 26).
   *
   * Apart van `mine`, want het is sinds de moderatie niet meer hetzelfde: een moderator mag ook de
   * reactie van iemand anders weghalen. De client hoeft het recht dus niet zelf na te rekenen —
   * één plek waar die regel staat, en dat is de server.
   */
  canDelete: z.boolean(),
  /** De @-vermeldingen in deze reactie, met naam, zodat de client ze kan markeren (Stap 26). */
  mentions: z.array(mentionCandidateSchema),
  /** Of ík deze reactie al meldde. Tweemaal melden heeft geen zin en hoort niet te kunnen. */
  reportedByMe: z.boolean(),
  /**
   * Hoeveel mensen deze reactie leuk vonden, en of ik erbij zat (SCH-1).
   *
   * **Aantallen, geen namen** (ADR-0014) — net als bij de like op een bericht. Het aantal komt uit
   * rijen en staat nergens als teller opgeslagen.
   */
  likeCount: z.number().int(),
  likedByMe: z.boolean(),
  /**
   * Zacht verwijderd, maar nog zichtbaar omdat er antwoorden onder hangen.
   *
   * Zonder deze toestand zou een verwijderde ouder zijn antwoorden meenemen, en dat is precies wat
   * het zachte verwijderen wilde voorkomen: wie een reactie weghaalt, laat geen gat vol
   * vraagtekens achter voor wie er al op antwoordde. `body` is dan leeg.
   */
  deleted: z.boolean(),
});
export type Comment = z.infer<typeof commentSchema>;

export const commentPageSchema = z.object({
  items: z.array(commentSchema),
  nextCursor: z.string().nullable(),
});
export type CommentPage = z.infer<typeof commentPageSchema>;

export const createCommentRequestSchema = z.object({
  body: z.string().trim().min(1, 'Een reactie mag niet leeg zijn.').max(2000),
  /**
   * De reactie waarop dit een antwoord is (SCH-1).
   *
   * Is die zelf een antwoord, dan hangt de server dit antwoord aan dezelfde ouder in plaats van
   * het te weigeren — de schrijver bedoelde "ik reageer hierop", en die bedoeling klopt nog
   * steeds.
   */
  parentId: z.guid().nullable().optional(),
  /**
   * De personen die in de tekst vermeld worden (Stap 26).
   *
   * De client stuurt de ids mee; de server kiest ze niet uit de tekst. Dat is bewust: "@Jan" is
   * dubbelzinnig in een bedrijf met drie Jannen, en een server die namen uit tekst leest, vermeldt
   * vroeg of laat de verkeerde. Wie niet in de doelgroep van het bericht zit, wordt door de server
   * stil weggefilterd — een vermelding naar iemand die het bericht niet mag zien, zou hem een
   * melding geven over iets wat hij niet kan openen.
   */
  mentions: z.array(z.guid()).max(20).default([]),
});
export type CreateCommentRequest = z.infer<typeof createCommentRequestSchema>;

/**
 * Het aantal reacties per bericht, voor de kaarten van een feedpagina.
 *
 * Eigen route en niet in de feed zelf: `communication` kent `interaction` niet, en een reactieteller
 * in het feedcontract zou die richting omdraaien. Eén extra aanroep per pagina is de prijs voor een
 * module die uitgezet kan worden.
 */
export const commentCountSchema = z.object({
  postId: z.guid(),
  count: z.number().int(),
});
export type CommentCount = z.infer<typeof commentCountSchema>;

export const commentCountListSchema = z.object({ items: z.array(commentCountSchema) });
export type CommentCountList = z.infer<typeof commentCountListSchema>;

/**
 * Eén reactie zoals ze op een kaart in de tijdlijn past (Extra 5).
 *
 * Magerder dan `commentSchema`, en dat is het punt: op de kaart kun je niets doen met een reactie
 * - niet liken, niet melden, niet verwijderen. Alles wat een knop zou voeden, ontbreekt hier dus,
 * zodat er geen knop kan ontstaan die de server niet dekt.
 */
export const commentPreviewSchema = z.object({
  id: z.guid(),
  authorId: z.guid(),
  authorName: z.string(),
  /**
   * De afdeling van de auteur, of `null`.
   *
   * Dezelfde gegevens als bij de afzender van een bericht (UI-2): de afdeling is geen veld dat een
   * bedrijf per medewerker zichtbaar of onzichtbaar zet - dat zijn het e-mailadres, de vestiging
   * en de eigen velden (SCH-3). Wie geen afdeling heeft, krijgt `null`.
   */
  department: z.string().nullable(),
  body: z.string(),
  createdAt: z.string().datetime(),
  likeCount: z.number().int(),
  /**
   * De naam van degene aan wie dit een antwoord is, of `null` bij een gewone reactie.
   *
   * Een naam en geen `parentId`: op de kaart staat geen boom, er staat "antwoord aan Lotte". De
   * ouder zelf staat er misschien niet eens bij - het zijn de laatste drie, niet een gesprek.
   */
  replyToName: z.string().nullable(),
});
export type CommentPreview = z.infer<typeof commentPreviewSchema>;

/** Het aantal reacties bij een bericht, met de laatste drie erbij (Extra 5). */
export const commentPreviewGroupSchema = z.object({
  postId: z.guid(),
  count: z.number().int(),
  /** Hoogstens drie, **oudste bovenaan**, zodat ze als een gesprek lezen. */
  latest: z.array(commentPreviewSchema).max(3),
  /** Of er nog gereageerd kan worden; staat het uit, dan toont de kaart "Gesloten" (Stap 26). */
  commentsEnabled: z.boolean(),
});
export type CommentPreviewGroup = z.infer<typeof commentPreviewGroupSchema>;

/**
 * Een melding die afgehandeld is (Extra 6, punt 5).
 *
 * Een geschiedenis en geen tweede wachtrij: wie beoordeelt, hoort te kunnen nakijken wat er
 * eerder met dezelfde persoon of hetzelfde bericht gebeurde - en bij een klacht achteraf is
 * "wie besliste wat, en wanneer" de enige vraag die telt.
 */
export const moderationDecisionSchema = z.object({
  commentId: z.guid(),
  postId: z.guid(),
  postTitle: z.string(),
  authorName: z.string(),
  /** Leeg bij een weggehaalde reactie: de tekst is weg, de beslissing blijft. */
  body: z.string(),
  reportCount: z.number().int(),
  reasons: z.array(z.string()),
  /** `removed` als de reactie weg is, `kept` als de moderator ze liet staan. */
  outcome: z.enum(['removed', 'kept']),
  decidedAt: z.string().datetime(),
  decidedByName: z.string().nullable(),
});
export type ModerationDecision = z.infer<typeof moderationDecisionSchema>;

export const moderationDecisionListSchema = z.object({
  items: z.array(moderationDecisionSchema),
});
export type ModerationDecisionList = z.infer<typeof moderationDecisionListSchema>;

export const commentPreviewListSchema = z.object({
  items: z.array(commentPreviewGroupSchema),
});
export type CommentPreviewList = z.infer<typeof commentPreviewListSchema>;

/**
 * Wat er met een bericht gebeurd is, voor wie het publiceerde (Extra 6, punt 2).
 *
 * Hetzelfde als de kaart in de tijdlijn toont, maar vanuit de andere kant bekeken: niet "wat zeggen
 * mijn collega's", maar "wat heeft mijn bericht losgemaakt". Daarom geen `likedByMe` - de
 * opsteller kijkt hier niet als lezer.
 */
export const postInteractionSummarySchema = z.object({
  postId: z.guid(),
  likeCount: z.number().int(),
  commentCount: z.number().int(),
  /** De laatste drie reacties, oudste bovenaan. */
  latest: z.array(commentPreviewSchema).max(3),
  /** Hoeveel reacties er openstaan in de moderatiewachtrij (Stap 26). */
  reportedCount: z.number().int(),
});
export type PostInteractionSummary = z.infer<typeof postInteractionSummarySchema>;
